import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { ESCAPE } from '@angular/cdk/keycodes';
import { CdkConnectedOverlay, CdkOverlayOrigin, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import { Component, Inject, Input, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { WINDOW } from '@app/common/app-tokens';
import { LinkModule } from '@app/directives/link.directive';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
  animations: [
    trigger('state', [
      state('initial, void, hidden', style({ opacity: 0, transform: 'scale(0)' })),
      state('visible', style({ transform: 'scale(1)' })),
      transition('* => visible', animate('200ms cubic-bezier(0, 0, 0.2, 1)', keyframes([
        style({ opacity: 0, transform: 'scale(0)', offset: 0 }),
        style({ opacity: 0.5, transform: 'scale(0.99)', offset: 0.5 }),
        style({ opacity: 1, transform: 'scale(1)', offset: 1 })
      ]))),
      transition('* => hidden', animate('100ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 0 }))),
    ])],
})
export class PopoverComponent implements OnInit, OnDestroy {

  @Input() originOverlay: CdkOverlayOrigin;
  @Input() isOpen = false;
  @ViewChild(CdkConnectedOverlay, { static: true }) connectedOverlay: CdkConnectedOverlay;
  element: HTMLElement;
  _manualListeners = new Map<string, EventListenerOrEventListenerObject>();
  private subscription: Subscription = new Subscription();
  overlayRef: OverlayRef;
  subscriptions = new Subscription();

  constructor(private platform: Platform, private focusTrap: ConfigurableFocusTrapFactory, @Inject(WINDOW) private window: Window) {
    if (!this.platform.IOS && !this.platform.ANDROID) {
      this._manualListeners
        .set('mouseenter', () => this.show())
        .set('mouseleave', (event) => this.hide(event))
        .set('focus', () => {
          this.isOpen = true;
          this.window.setTimeout(() => this.focus(this.connectedOverlay.overlayRef.overlayElement), 200);
        });
    }
  }

  mouseLeave() {
    this.isOpen = false;
  }

  ngOnInit() {
    this.element = this.originOverlay.elementRef.nativeElement as HTMLElement;
    this._manualListeners.forEach((listener, event) => this.element.addEventListener(event, listener));
  }

  show() {
    this.isOpen = true;
  }

  focus(overlayElement: HTMLElement) {
    if (overlayElement) {
      if (this.connectedOverlay.overlayRef.hasAttached) {
        const focusTrap = this.focusTrap.create(overlayElement);    // creates a focus trap region
        focusTrap.focusInitialElement();
      }

      this.subscriptions.add(
        this.connectedOverlay
          .overlayRef
          .keydownEvents()
          .subscribe((event: KeyboardEvent) => {
            if (event.key) {
              if (event.key === 'Escape') {
                this.connectedOverlay.overlayRef.detach();
                const element = this.element;
                this.window.setTimeout(() => element.focus(), 0);
              }
            } else {
              /* tslint:disable */
              if (event.keyCode === ESCAPE) {
                /* tslint:enable */
                this.connectedOverlay.overlayRef.detach();
                const element = this.element;
                this.window.setTimeout(() => element.focus(), 0);
              }
            }
          }));
    }
  }

  hide(event) {
    const element = event.toElement || event.relatedTarget;
    if (element) {
      const overlayElement = this.connectedOverlay.overlayRef?.overlayElement;
      if (overlayElement) {
        const leaveToPopover = overlayElement.contains(element) || overlayElement === element;
        if (leaveToPopover) {
          this.isOpen = true;
        } else {
          this.isOpen = false;
        }
      } else {
        this.isOpen = false;
      }
    } else {
      this.isOpen = false;
    }
  }

  ngOnDestroy(): void {
    if (this.element) {
      this._manualListeners.forEach((listener, event) => {
        this.element.removeEventListener(event, listener);
      });
    }
    this.subscription && this.subscription.unsubscribe();
  }
}

@NgModule({
  declarations: [PopoverComponent],
  exports: [PopoverComponent],
  imports: [
    CommonModule,
    OverlayModule,
    LinkModule,
    MatButtonModule
  ]
})
export class PopoverModule { }
